import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, User, Phone, ArrowRight } from "lucide-react";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import SEO from "@/components/storefront/SEO";

export default function AccountRegister() {
  const nav = useNavigate();
  const { signUp } = useCustomerAuth();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    marketing_consent: true,
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    const res = await signUp(form);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    nav("/account", { replace: true });
  };

  return (
    <div className="mx-auto grid min-h-[70vh] max-w-md px-6 py-14" data-testid="account-register-page">
      <SEO title="Create account" description="Create a Luna. Creative Home account." noindex />
      <div className="m-auto w-full">
        <div className="mb-2 text-xs uppercase tracking-[0.3em] text-[#2D2823]">Account</div>
        <h1 className="font-display text-5xl leading-tight md:text-[64px]">Create <span className="font-display-soft">your account</span></h1>
        <p className="mt-3 text-sm text-[#2D2823]">Save addresses, track orders, and shop faster next time.</p>

        <form onSubmit={submit} className="mt-10 space-y-4">
          <Field icon={User} label="Full name" autoComplete="name" value={form.name} onChange={(v) => set("name", v)} testId="reg-name" required />
          <Field icon={Mail} label="Email" type="email" autoComplete="email" value={form.email} onChange={(v) => set("email", v)} testId="reg-email" required />
          <Field icon={Phone} label="Phone (optional)" type="tel" autoComplete="tel" value={form.phone} onChange={(v) => set("phone", v)} testId="reg-phone" />
          <Field icon={Lock} label="Password (min 6 chars)" type="password" autoComplete="new-password" value={form.password} onChange={(v) => set("password", v)} testId="reg-password" required />

          <label className="flex items-start gap-2 text-xs text-[#2D2823]">
            <input
              type="checkbox"
              checked={form.marketing_consent}
              onChange={(e) => set("marketing_consent", e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-[#7D8471]"
              data-testid="reg-marketing"
            />
            <span>Send me thoughtful product news. No spam, unsubscribe anytime.</span>
          </label>

          {error && <div className="rounded-md bg-rose-50 p-3 text-xs text-rose-700" data-testid="reg-error">{error}</div>}

          <button
            type="submit"
            disabled={busy}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#1A1A1A] px-6 py-3.5 text-sm text-white transition-colors hover:bg-[#7D8471] disabled:opacity-60"
            data-testid="reg-submit"
          >
            {busy ? "Creating…" : (<>Create account <ArrowRight size={14} /></>)}
          </button>

          <p className="text-center text-xs text-[#2D2823]">
            Already have an account?{" "}
            <Link to="/account/login" className="underline decoration-[#7D8471] underline-offset-4 hover:text-[#1A1A1A]" data-testid="reg-login-link">Sign in</Link>
          </p>
          <p className="text-center text-[10px] text-[#2D2823]">
            By creating an account you agree to our{" "}
            <Link to="/pages/terms" className="underline">Terms</Link> and{" "}
            <Link to="/pages/privacy" className="underline">Privacy Policy</Link>.
          </p>
        </form>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, type = "text", value, onChange, testId, required, autoComplete }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] uppercase tracking-[0.2em] text-[#2D2823]">{label}</span>
      <span className="relative block">
        <Icon size={14} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#2D2823]" />
        <input
          type={type}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          className="w-full rounded-full border border-[#DDD6C5] bg-white py-3 pl-11 pr-4 text-sm outline-none focus:border-[#1A1A1A]"
          data-testid={testId}
        />
      </span>
    </label>
  );
}
